// @ts-strict-ignore
import { Component, HostBinding, Input } from '@angular/core';
import { take } from 'rxjs';
import {
  createValueMatrix,
  isSingleValueMatrix,
} from '../../../../../../functions/board-view/datagrid/create-value-matrix';
import { Language } from '../../../../../../model/enums/language';
import { LanguageService } from '../../../../../../services/language.service';
import { LocalizeNumberPipe } from '../../../../pipes/localize-number.pipe';
import { GlobalIcon, GlobalIconColour, GlobalIconSize } from '../../../icon/icon.constants';
import { TableService } from '../../table.service';
import { TableColumn, TableParentColumn, TableRow } from '../../types';

@Component({
    selector: 'app-basic-cell',
    templateUrl: './basic-cell.component.html',
    standalone: false
})
export class BasicCellComponent {
  @Input() column: TableColumn | TableParentColumn;
  @Input() disabled: boolean = false;
  @Input() ignoreReadOnly: boolean = false;
  @Input() index: number;
  @Input() parent: TableParentColumn;
  @Input() row: TableRow;

  activeCell: boolean = false;
  cellClasses: {
    parent?: boolean;
    noedit?: boolean;
    editable?: boolean;
    active?: boolean;
  } = {};
  cellValue: string | boolean;
  decimals: number;
  globalIcons = GlobalIcon;
  globalIconColours = GlobalIconColour;
  globalIconSizes = GlobalIconSize;
  inputType: 'text' | 'number' | 'boolean';
  isLeftColumnHeading: boolean;
  lang: Language;
  placeholder: string = '';
  showFalseIcon: boolean;

  @HostBinding('class.disabled') get isDisabled() {
    return this.column.disabled;
  }

  get isCellValueBoolean() {
    return typeof this.cellValue === 'boolean';
  }

  constructor(
    private _tableService: TableService,
    private _languageService: LanguageService,
    private _localizeNumberPipe: LocalizeNumberPipe
  ) {
    this.decimals = this._tableService.decimals;
    this.isLeftColumnHeading = this._tableService.isLeftColumnHeading;
    this.showFalseIcon = this._tableService.showFalseIcon;
    this.lang = this._languageService.language;
  }

  ngOnInit() {
    this._setCellData(this.row);
    this._setColumnInputType();
    if (this.row.placeholder) {
      this._setInputPlaceholder();
    }
    this._tableService.editedRow.subscribe((row) => {
      const findRow = row.rows.find((row) => row.id === this.row.id);
      if (findRow) {
        this._setCellData(findRow);
      }
    });
  }

  ngOnChanges() {
    this._setCellClasses();
    this._setCellData(this.row);
  }

  getCellValue(): number | string | boolean {
    if (Number(this.cellValue) && !this.activeCell) {
      return this._localizeNumberPipe.transform(Number(this.cellValue), this.lang, this.decimals);
    } else {
      return this.cellValue;
    }
  }

  isCellActive(cell: { colId: string; rowId: string }): boolean {
    if (!this.activeCell) {
      return false;
    }

    return cell.colId === this.column.id && cell.rowId === this.row.id;
  }

  onBlur() {
    this.row.data[this.column.id] = this.cellValue;
    this._sanitizeCellValue({ colId: this.column.id, rowId: this.row.id });
    this._tableService.editRow([this.row]);
    this.activeCell = undefined;
    this._setColumnInputType();
    this._setCellClasses();
  }

  onEnter(event) {
    this.onBlur();
    const cell = event.target.parentElement.parentElement as Element;

    const editableCells = document.getElementsByClassName('editable');
    const findElement = Array.from(editableCells).findIndex((el) => (el === cell ? true : false));

    if (editableCells[findElement + 1]) {
      (editableCells[findElement + 1].children[0].children[0] as HTMLElement).focus();
    }
  }

  onFocus() {
    this.activeCell = true;
    this._setColumnInputType();
  }

  onPaste(event: ClipboardEvent) {
    const values = createValueMatrix(event.clipboardData.getData('text'), true);
    if (!this.activeCell || isSingleValueMatrix(values)) {
      return;
    }
    event.preventDefault();
    this._pasteValuesInCell({ colId: this.column.id, rowId: this.row.id }, values);
  }

  onToggleChildren(item: TableParentColumn | TableRow) {
    item.childrenActive = !item.childrenActive;
  }

  setCellValue(value: string | boolean): void {
    this.cellValue = value;
  }

  private _pasteValuesInCell(cell: { rowId: string; colId: string }, values: string[][]): void {
    this._tableService.data.pipe(take(1)).subscribe((data) => {
      const cellRowIndex = data.rows.findIndex((row) => row.id === cell.rowId);
      const cellColumnIndex = data.columns.findIndex((col) => col.id === cell.colId);
      let editedRows: TableRow[] = [];
      values.forEach((valueRow, r) => {
        const row = data.rows[cellRowIndex + r];
        valueRow.forEach((value, c) => {
          const column = data.columns[cellColumnIndex + c];
          if (row && column) {
            if (this.column.editable) {
              if (column.editConfig.inputType === 'number') {
                Number(value) ? (row.data[column.id] = value) : null;
              } else {
                row.data[column.id] = value;
              }
              this._sanitizeCellValue({ rowId: row.id, colId: column.id });
              editedRows = [...editedRows, row];
            }
          }
        });
      });
      this._tableService.editRow(editedRows);
    });
  }

  private _sanitizeCellValue(cell: { rowId: string; colId: string }) {
    if (this.column.editConfig?.sanitize) {
      this.row.data[cell.colId] = this.column.editConfig?.sanitize(this.row.data[cell.colId]);
    }
  }

  private _setCellClasses() {
    this.cellClasses = {
      parent: this.row.children && this.index === 0,
      noedit:
        (!this.column.editable && !this.parent?.editable) ||
        (this.column.editable && 'children' in this.column && !('childrenActive' in this.column)),
      editable: (this.column.editable && !('children' in this.column)) || this.parent?.editable,
      active: this.isCellActive({ colId: this.column.id, rowId: this.row.id }),
    };
  }

  private _setCellData(row: TableRow) {
    this.cellValue = row.data[this.column.id];
  }

  private _setColumnInputType() {
    const type = this.column.editConfig?.inputType;
    if (type === 'number') {
      this.inputType = this.activeCell ? 'number' : 'text';
    } else if (type === 'text' || type == 'boolean') {
      this.inputType = type;
    }
  }

  private _setInputPlaceholder(): void {
    this.placeholder = this.row.placeholder[this.column.id];
  }
}
